/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../share/util/individualDiagnose';

import { checkRegisteredVin } from './read';
import { writeAndCheckVin } from './write';
import { exitLogic } from './exit';
import { PHASE, DESTINATION } from './constants';

export default {
  name: 'requirement004',
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      errorMsg: '',
      inProgressMsg: '',
      watcher: null,
      isInitialized: false,
      inputVin: '',
      validateVin: undefined,
      selectedDestination: '',
      disableDestination: false,
      modal_requirement_004: false,
      isFirstAPI: true
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
    isSpecialTransition: { type: Boolean, default: false, required: false },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId,
      vehicleInfo: state => state.cache.vehicleInfo
    }),
    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.FAILED:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.START:
        case PHASE.WRITING:
        default:
          return 'secondary';
      }
    },
    /** 仕向 */
    destinations() {
      return [
        { key: 'label_domestic', value: DESTINATION.DOMESTIC },
        { key: 'label_na', value: DESTINATION.NA },
        { key: 'label_ch', value: DESTINATION.CH },
        { key: 'label_other', value: DESTINATION.OTHER }
      ];
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),
    ...mapActions('cache', ['cacheVin']),
    ...mapActions('vehicle', ['setVehicleVin']),

    /** 初期化処理 */
    init() {
      // 入力VINの初期値は車両情報のVIN
      this.inputVin = this.vehicleInfo.vehicle.vin.vin;
      this.errorMsg = '';
      this.inProgressMsg = '';
      this.validateVin = undefined;
      this.selectedDestination = '';
      this.disableDestination = false;
      this.isInitialized = false;
      this.modal_requirement_004 = true;
      this.isFirstAPI = true;
    },

    /**
     * 終了処理
     */
    async exitProcess() {
      if (this.isInitialized || this.isSpecialTransition) {
        // 通信を1度でも行った、または特殊機能から遷移してきた場合、exitLogic を呼ぶ
        this.isInitialized = false;
        await exitLogic(this);
      }
      // Wizardを閉じる
      this.closeWizard();
    },


    /** Wizardを閉じる */
    closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_004 = false;
    },

    /** 登録処理 */
    async registerVin() {
      // 初期化
      this.inputVin = this.inputVin.toUpperCase();
      this.validateVin = undefined;
      // バリデーションを確認し、該当した場合はreturn
      if (this.checkValidate(this.inputVin)) return;
      try {
        this.isLoading = true;
        // 車両通信中を表示
        // 通信を開始したので、初期化フラグを有効にする
        this.isInitialized = true;
        this.inProgressMsg = `${this.i18nWsKey}.label_please_wait`;
        this.phase = PHASE.WRITING;

        // 特殊機能[SP.WS.ECM3]からの遷移の場合、VIN登録済み判定を行わない
        if (!this.isSpecialTransition) {
          // VIN読み出し（エラー時は呼び出し先でエラーを出すので、そのまま終了）
          const isReadVinSuccess = await checkRegisteredVin(this);
          if (!isReadVinSuccess) {
            return;
          }
        }

        // VIN登録中を表示
        this.inProgressMsg = `${this.i18nWsKey}.label_register_vin`;

        // VINデータ書き込み実行
        await writeAndCheckVin(this);
      } finally {
        this.isLoading = false;
      }
    },

    /** その他エラーの表示 */
    showUnexpectedError() {
      this.errorMsg = 'individual.error_communication_error';
      this.phase = PHASE.FAILED;
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /**
     * 選択した仕向に応じたバリデーションを確認する
     * @param vin 対象VIN
     * @returns boolean バリデーション結果(trueの場合エラー)
     */
    checkValidate(vin) {
      // vinが空の場合、エラーを出す
      if (!vin) {
        this.validateVin = { valid: true, msg: this.$t(`${this.i18nWsKey}.alert_validate_required`) };
        return true;
      }

      switch (this.selectedDestination) {
        case DESTINATION.DOMESTIC:
          return this.checkValidationJp(vin);
        case DESTINATION.NA:
        case DESTINATION.CH:
          return this.checkValidationNaCh(vin);
        case DESTINATION.OTHER:
          return this.checkValidationOther(vin);
        default:
          return true;
      }
    },

    /**
     * 日本VIN検証 
     * @param vin 対象VIN
     */
    checkValidationJp(vin) {
      // 不正文字列の確認
      if (this.checkInvalidCharacter(vin, true)) {
        this.validateVin = { valid: true, msg: this.$t(`${this.i18nWsKey}.alert_validate_invalid_pattern`) };
        return true;
      }
      // validation1. 4桁目の確認
      // 4桁目が'-'(ハイフン)でない場合、VINが異常と判断する
      if (vin[3] !== '-') {
        this.validateVin = { valid: true, msg: this.$t(`${this.i18nWsKey}.alert_validate_invalid_pattern`) };
        return true;
      }

      // validation2. 10桁かどうか
      // 文字列が10桁でない場合、VINが異常と判断する
      if (vin.length !== 10) {
        this.validateVin = { valid: true, msg: this.$t(`${this.i18nWsKey}.alert_validate_invalid_pattern`) };
        return true;
      }

      // validation3. 5～10桁目が0～9の数字であるか
      const lastNumber = vin.slice(4, 10);
      const lastRegExp = /^(\d+)$/i;
      // 5～10桁目が0～9の数字以外が入っている場合、VINが異常と判断する
      if (!lastRegExp.test(lastNumber)) {
        this.validateVin = { valid: true, msg: this.$t(`${this.i18nWsKey}.alert_validate_invalid_pattern`) };
        return true;
      }

      // 3つの条件に当てはまる場合は、VINが正常と判断する
      this.validateVin = undefined;
      return false;
    },

    /**
     * 北米/中国VIN検証 
     * @param vin 対象VIN
     */
    checkValidationNaCh(vin) {
      // 不正文字列の確認
      if (this.checkInvalidCharacter(vin, false)) {
        this.validateVin = { valid: true, msg: this.$t(`${this.i18nWsKey}.alert_validate_invalid_pattern`) };
        return true;
      }
      // validation. 17桁かどうか
      // 文字列が17桁以外の場合、VINが異常と判定する
      if (vin.length !== 17) {
        this.validateVin = { valid: true, msg: this.$t(`${this.i18nWsKey}.alert_validate_max_length`, ['17']) };
        return true;
      }

      // チェックディジット判定を行い、正しければVINが正常と判定する
      if (this.checkDigits(vin)) {
        this.validateVin = undefined;
        return false;
      } else {
        this.validateVin = { valid: true, msg: this.$t(`${this.i18nWsKey}.alert_validate_invalid_pattern`) };
        return true;
      }
    },

    /**
     * その他VIN検証
     * @param vin 対象VIN
     */
    checkValidationOther(vin) {
      // 不正文字列の確認
      if (this.checkInvalidCharacter(vin, false)) {
        this.validateVin = { valid: true, msg: this.$t(`${this.i18nWsKey}.alert_validate_invalid_pattern`) };
        return true;
      }
      // validation. 17桁かどうか
      // 文字列が17桁以外の場合、VINが異常と判定する
      if (vin.length !== 17) {
        this.validateVin = { valid: true, msg: this.$t(`${this.i18nWsKey}.alert_validate_max_length`, ['17']) };
        return true;
      }

      // validation2. 先頭3桁の確認
      const preNumber = vin.slice(0, 3);
      const preRegExp = /^(JF1|JF2|JF3|4S3|4S4|PLP|MMR|JT2| + + )$/i;
      // 先頭の3桁が該当する文字列の場合、VINが正常と判断する
      if (preRegExp.test(preNumber)) {
        this.validateVin = undefined;
        return false;
      } else {
        this.validateVin = { valid: true, msg: this.$t(`${this.i18nWsKey}.alert_validate_invalid_pattern`) };
        return true;
      }
    },

    /**
     * 使用不可な文字列が含まれていないかの確認関数
     * @param vin 対象VIN
     * @param isDomestic 国内か否か
     */
    checkInvalidCharacter(vin, isDomestic) {
      // \x20から\x7eが制御文字を除く通常の半角英数字なので、それ以外の場合は弾く
      const halfReqExp = new RegExp(/^[\x20-\x7e]*$/);
      // IOQは1や0と混合するので除外対象。国内の場合は-(ハイフン)を許容
      const invalidCharRegExp = isDomestic ? /I|O|Q/i : /I|O|Q|-/i;
      return invalidCharRegExp.test(vin) || !halfReqExp.test(vin);
    },

    /**
     * チェックディジット判定
     * @param vin 
     * @returns 判定結果
     */
    checkDigits(vin) {
      // 重み換算用文字列
      const weightMap = '0123456789X';
      // 各桁数に対する重み(1~17桁)
      const weights = '8765432X098765432';
      // 各桁の合計値
      let sum = 0;
      for (let i = 0; i < 17; i++) {
        // 桁毎の文字列を対応する数字に換算する
        const replaceValue = this.transliterate(vin[i]);
        // 0未満の場合、非対応文字列のためfalse
        if (replaceValue < 0) {
          return false;
        }
        // 換算した結果と各桁の重みを乗算し、加算させる
        sum += replaceValue * weightMap.indexOf(weights[i]);
      }
      // 換算した結果と各桁の重みを乗算し、加算させた値を11で割った余りがチェックディジット値となり、
      // その値がvin入力値の9桁目と等しい場合、trueを返す
      return weightMap[sum % 11] === vin[8];
    },

    /**
     * チェックディジット用、文字換算関数
     * @param char 換算する文字 
     * @returns 換算結果
     */
    transliterate(char) {
      // 対象文字列の位置をindexOfで返し、10で割ったときの余りが換算された結果となっている
      // -1 が返却される場合、不正な文字が使われている判定となる
      return '0123456789.ABCDEFGH..JKLMN.P.R..STUVWXYZ'.indexOf(char) % 10;
    },

    /** 登録を確定 */
    async confirmRegister() {
      // 終了処理
      this.validateVin = undefined;
      this.exitProcess();
    },

    /** 登録をキャンセル */
    cancelRegister() {
      // 最終確認で登録をキャンセルした場合、ラジオボタンを変更できなくした状態でスタート画面に戻す
      // 特殊機能から遷移している場合はラジオボタンを変更可能とする
      this.validateVin = undefined;
      this.disableDestination = !this.isSpecialTransition;
      // 初期値は車両情報のVINに戻す
      this.inputVin = this.vehicleInfo.vehicle.vin.vin;
      this.phase = PHASE.START;
    }
  },

  mounted() {
    // 初期化
    this.init();
  },
};